import React, { useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalytics } from '../analytics/use-analytics';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { useGetExperimentValue } from '../services/experiment-context';
import { isEligibleForChooseYourEdition } from '../services/is-eligible-for-choose-your-edition';
import { isEligibleForConfluenceBuyReverseTrialExperiment } from '../services/is-eligible-for-confluence-buy-reverse-trials';
import { isEligibleForManualFeatureOnboarding } from '../services/is-eligible-for-manual-feature-onboarding';
import { isEligibleForServCoUplift } from '../services/is-eligible-for-service-collection-uplift';
import { isEligibleForStorageHardLimit } from '../services/is-eligible-for-storage-hard-limit';
import { isProductOneOf } from '../services/is-product-one-of';

import { IneligibleAnalytics } from './audience-and-timing';
import { AutomationFreeLimitModal } from './dialogs/automation-free-limit-modal';
import { CollectionAddPaymentDetailsPopup } from './dialogs/collection-add-payment-details-popup';
import { CollectionChangePlanPopup } from './dialogs/collection-change-plan-popup';
import { FeatureOnboardingModal } from './dialogs/feature-onboarding-modal';
import { FreeChooseYourEditionModal } from './dialogs/free-choose-your-edition-modal';
import { FreeToStandardModal } from './dialogs/free-to-standard-modal';
import { PaymentDetailsPopup } from './dialogs/payment-details-popup';
import { PlanSelectionPageModal } from './dialogs/plan-selection-page-modal';
import { PremiumAddPaymentModal } from './dialogs/premium-add-payment-modal';
import { PremiumPopup } from './dialogs/premium-popup';
import { ReverseTrialPopupDialog } from './dialogs/reverse-trial-popup';
import { StandardAddPaymentModal } from './dialogs/standard-add-payment-modal';
import { StorageHardLimitModal } from './dialogs/storage-hard-limit-modal';
import { AddPaymentLink } from './links/add-payment-link';
import { JiraStandardTrialOverviewLink } from './links/jira-standard-trial-overview-link';
import { PaymentFlowLink } from './links/payment-flow-link';
import { ServiceCollectionLink } from './links/service-collection-link';

/**
 * Sends an event when there is no action available for the customer to render. This is an error state
 * which means that audience and timing has passed a customer as eligible when they shouldn't have.
 *
 * Next iteration on this would be to return a safe default action for all customers
 */
export function NoAction() {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'actionsAndContent',
			actionSubjectId: 'editionAwareness',
			action: 'missed',
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent]);

	return null;
}

function FreeEdition() {
	const context = useEditionAwareness();
	const { product, isInCollection, userRoles, automationFreeLimitStatus } = context;
	const { getExperimentValue } = useGetExperimentValue();

	if (
		isProductOneOf(product, ['jira', 'confluence']) &&
		isInCollection &&
		userRoles?.includes('BILLING_ADMIN')
	) {
		return <PlanSelectionPageModal type="twc" />;
	}

	// block any EA to be shown if the customer is in teamwork collection
	if (isInCollection && isProductOneOf(product, ['jira', 'confluence'])) {
		return null;
	}

	if (fg('twcg-1076_jira_automation_limits_kill_switch')) {
		if (automationFreeLimitStatus) {
			// These two statuses change the button content, and we need to fire exposure
			const fireExperimentExposure =
				automationFreeLimitStatus === 'BREACHED' ||
				automationFreeLimitStatus === 'PROJECTED_TO_BREACH';

			const cohort = getExperimentValue({
				statsigParams: {
					experimentName: 'twcg-1076_jira_automation_limits_experiment',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
					options: {
						fireExperimentExposure,
					},
				},
				trackEventParameters: {
					trackEventGrowthInitiative: 'luna',
				},
			});

			if (cohort === 'variation') {
				return <AutomationFreeLimitModal />;
			}
		}
	}

	if (isEligibleForStorageHardLimit(context)) {
		return <StorageHardLimitModal />;
	}

	if (isEligibleForChooseYourEdition(context)) {
		const cohort = getExperimentValue({
			statsigParams: {
				experimentName: 'confluence_free_choose_your_edition_experiment',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: false,
				},
			},
		});

		if (cohort === 'variation') {
			return <FreeChooseYourEditionModal />;
		}
	}

	return <FreeToStandardModal />;
}

function StandardEdition() {
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();
	const {
		product,
		isInTrial,
		isInPredunning,
		invoiceableStatus,
		canAddPaymentDetails,
		isInCollection,
		userRoles,
		addPaymentDetailsExperienceUrl,
		userEmail,
	} = context;

	if (
		isProductOneOf(product, ['jira', 'confluence']) &&
		isInCollection &&
		userRoles?.includes('BILLING_ADMIN')
	) {
		if (isInTrial) {
			if (invoiceableStatus === 'NOT_INVOICEABLE' && canAddPaymentDetails) {
				// Teamwork Collection with no PDE in trial
				return <PlanSelectionPageModal type="twc" />;
			}

			return <CollectionChangePlanPopup />;
		}

		if (isInPredunning) {
			return <CollectionAddPaymentDetailsPopup />;
		}
	}

	// block any EA to be shown if the customer is in teamwork collection
	if (isInCollection && isProductOneOf(product, ['jira', 'confluence'])) {
		return null;
	}

	// Service Collection Uplift for JSM Standard
	if (isEligibleForServCoUplift(context)) {
		return <ServiceCollectionLink />;
	}

	if (
		isEligibleForManualFeatureOnboarding(context) &&
		getExperimentValue({
			statsigParams: {
				experimentName: 'manual_trial_w_pde_onboarding',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
		}) === 'variation'
	) {
		return <FeatureOnboardingModal />;
	}

	if (
		(isInTrial || isInPredunning) &&
		invoiceableStatus === 'NOT_INVOICEABLE' &&
		canAddPaymentDetails
	) {
		if (fg('ea_to_osb_kill_switch')) {
			if (isProductOneOf(product, ['jira', 'confluence']) && addPaymentDetailsExperienceUrl) {
				const osbEACohort = getExperimentValue({
					statsigParams: {
						experimentName: 'ea_to_osb_experiment',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
						options: {
							fireExperimentExposure: false,
						},
					},
				});

				if (osbEACohort === 'variation') {
					return <PaymentFlowLink href={addPaymentDetailsExperienceUrl} />;
				}
			}
		}

		if (
			isInTrial &&
			(product === 'jira-service-management' || product === 'jira-product-discovery')
		) {
			return <StandardAddPaymentModal />;
		}

		// Jira Standard - Site admins get routed to JSTO, others to payment popup
		if (product === 'jira' && isInTrial && userRoles?.includes('SITE_ADMIN') && userEmail) {
			return !fg('ea_to_jsto_kill_switch') ? (
				<JiraStandardTrialOverviewLink />
			) : (
				<PaymentDetailsPopup />
			);
		}

		// If the site admin doesn't have an email then we fallback to the non site admin experience
		if (product === 'jira' && isInTrial && (!userRoles?.includes('SITE_ADMIN') || !userEmail)) {
			return <PaymentDetailsPopup />;
		}

		if (product === 'confluence') {
			return <StandardAddPaymentModal />;
		}

		if (
			addPaymentDetailsExperienceUrl &&
			isInPredunning &&
			isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery'])
		) {
			return <AddPaymentLink href={addPaymentDetailsExperienceUrl} />;
		}
	}

	if (isEligibleForManualFeatureOnboarding(context)) {
		// Customers that are eligible (have payment details) would not see any other standard button
		// We're returning early with the ineligible analytics here to not fire the no action event which
		// is used for monitoring
		return <IneligibleAnalytics />;
	}

	return <NoAction />;
}

function PremiumEdition() {
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();

	const {
		product,
		isInTrial,
		isInPredunning,
		isInReverseTrial,
		isInCollection,
		addPaymentDetailsExperienceUrl,
		invoiceableStatus,
		canAddPaymentDetails,
		userRoles,
	} = context;

	// make sure twc is always shown first before jira reverse trial
	// otherwise, jira reverse trial will be shown with collection

	// addition check on invoiceableStatus and canAddPaymentDetails
	// to block any EA to be shown if the customer is in a collection
	// * confluence premium is showing EA regardless which needs to be blocked from collection
	if (
		isProductOneOf(product, ['jira', 'confluence']) &&
		isInCollection &&
		userRoles?.includes('BILLING_ADMIN')
	) {
		if (isInTrial) {
			if (invoiceableStatus === 'NOT_INVOICEABLE' && canAddPaymentDetails) {
				// Teamwork Collection with no PDE in trial
				return <PlanSelectionPageModal type="twc" />;
			}

			return <CollectionChangePlanPopup />;
		}

		if (isInPredunning) {
			return <CollectionAddPaymentDetailsPopup />;
		}
	}

	// block any EA to be shown if the customer is in a TWC collection
	if (isInCollection && isProductOneOf(product, ['jira', 'confluence'])) {
		return null;
	}

	// Service Collection Uplift for JSM Premium
	if (isEligibleForServCoUplift(context)) {
		return <ServiceCollectionLink />;
	}

	if (
		isEligibleForManualFeatureOnboarding(context) &&
		getExperimentValue({
			statsigParams: {
				experimentName: 'manual_trial_w_pde_onboarding',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
		}) === 'variation'
	) {
		return <FeatureOnboardingModal />;
	}

	if (
		product === 'jira' &&
		(isInTrial || isInPredunning) &&
		isInReverseTrial &&
		userRoles?.includes('BILLING_ADMIN') &&
		fg('jira_reverse_trial_ea_updates_during_trial_gate')
	) {
		const rtCohort = getExperimentValue({
			statsigParams: {
				experimentName: 'jira_reverse_trial_ea_updates_during_trial',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'transmuters',
			},
		});

		if (rtCohort === 'variation') {
			return <PlanSelectionPageModal type="reverse-trial" />;
		}
	}

	if (product === 'jira' && isInTrial && isInReverseTrial && addPaymentDetailsExperienceUrl) {
		return <ReverseTrialPopupDialog />;
	}

	if (product === 'jira' && isInTrial && !isInReverseTrial && addPaymentDetailsExperienceUrl) {
		return <PaymentDetailsPopup />;
	}

	if (
		isInPredunning &&
		isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery']) &&
		addPaymentDetailsExperienceUrl
	) {
		return <AddPaymentLink href={addPaymentDetailsExperienceUrl} />;
	}

	if (
		isEligibleForConfluenceBuyReverseTrialExperiment(context) &&
		fg('growth_confluence_buy_reverse_trials_fg')
	) {
		const abCohort = getExperimentValue({
			statsigParams: {
				experimentName: 'growth_confluence_buy_reverse_trials_ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'luna',
				trackEventAttributes: {
					touchpointId: 'actions-and-content',
				},
			},
		});

		if (abCohort === 'variation') {
			return <PlanSelectionPageModal type="reverse-trial" />;
		}
	}

	if (product === 'confluence' && (isInTrial || isInPredunning)) {
		return <PremiumPopup />;
	}

	if (isInTrial && isProductOneOf(product, ['jira-service-management', 'jira-product-discovery'])) {
		return <PremiumAddPaymentModal />;
	}

	if (isEligibleForManualFeatureOnboarding(context)) {
		// Customers that are eligible (have payment details) would not see any other standard button
		// We're returning early with the ineligible analytics here to not fire the no action event which
		// is used for monitoring
		return <IneligibleAnalytics />;
	}

	return <NoAction />;
}

/**
 * Covers what button and action we show to the customer.
 *
 * Experiments here should just modify what is shown to the customer not if it is shown to the customer.
 */
export function ActionsAndContent(): React.JSX.Element {
	const { edition } = useEditionAwareness();

	if (edition === 'free') {
		return <FreeEdition />;
	}

	if (edition === 'standard') {
		return <StandardEdition />;
	}

	if (edition === 'premium') {
		return <PremiumEdition />;
	}

	return <NoAction />;
}
